import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ClientSpendingByType,
  ReportAverageTicket,
  ReportSchedules,
  SalesReport,
  StatisticalReport,
  TotalSpentByClient,
} from "@/types";
import {
  fetchAverageTicket,
  fetchClientSpendingByType,
  fetchReportSchedules,
  fetchSalesReport,
  fetchStatisticalReport,
  fetchTotalSpentByClient,
  fetchGrafics,
} from "./thunks";

interface PaginatedReportSchedules {
  data: ReportSchedules[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedAverageTicket {
  data: ReportAverageTicket[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedSalesReport {
  data: SalesReport[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedTotalSpentByClient {
  data: TotalSpentByClient[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedClientSpendingByType {
  data: ClientSpendingByType[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedStatisticalReport {
  data: StatisticalReport;
  total?: number;
  currentPage?: number;
  perPage?: number;
}

const emptyTotals = {
  total_sales: 0,
  open_tickets_value: 0,
  closed_tickets_value: 0,
  total_service_sales: 0,
  total_product_sales: 0,
  total_ticket_count: 0,
  total_pending_tickets: 0,
  total_closed_tickets: 0,
  total_client_no_show: 0,
};

interface ReportsState {
  grafics: { data: any[] };
  reportSchedules: PaginatedReportSchedules;
  averageTicket: PaginatedAverageTicket;
  salesReport: PaginatedSalesReport;
  totalSpentByClient: PaginatedTotalSpentByClient;
  clientSpendingByType: PaginatedClientSpendingByType;
  statisticalReport: PaginatedStatisticalReport;
  loading: {
    reportSchedules: boolean;
    averageTicket: boolean;
    salesReport: boolean;
    totalSpentByClient: boolean;
    clientSpendingByType: boolean;
    statisticalReport: boolean;
  };
  error: string | null;
}

const initialState: ReportsState = {
  grafics: { data: [] },
  reportSchedules: { data: [] },
  averageTicket: { data: [] },
  salesReport: { data: [] },
  totalSpentByClient: { data: [] },
  clientSpendingByType: { data: [] },
  statisticalReport: {
    data: {
      totals: {
        total_sales: 0,
        open_tickets_value: 0,
        closed_tickets_value: 0,
        total_service_sales: 0,
        total_product_sales: 0,
        total_ticket_count: 0,
        total_pending_tickets: 0,
        total_closed_tickets: 0,
        total_client_no_show: 0,
      },
      detailed: [],
    },
  },
  loading: {
    reportSchedules: false,
    averageTicket: false,
    salesReport: false,
    totalSpentByClient: false,
    clientSpendingByType: false,
    statisticalReport: false,
  },
  error: null,
};

const ReportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    clearReports: (state) => {
      state.reportSchedules = { data: [] };
      state.averageTicket = { data: [] };
      state.salesReport = { data: [] };
      state.totalSpentByClient = { data: [] };
      state.clientSpendingByType = { data: [] };
      state.grafics = { data: [] };
      state.loading = {
        reportSchedules: false,
        averageTicket: false,
        salesReport: false,
        totalSpentByClient: false,
        clientSpendingByType: false,
        statisticalReport: false,
      };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchReportSchedules
      .addCase(fetchReportSchedules.pending, (state) => {
        state.loading.reportSchedules = true;
        state.error = null;
      })
      .addCase(
        fetchReportSchedules.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.reportSchedules = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.reportSchedules = { data: payload.data };
          } else {
            // Dados paginados
            state.reportSchedules = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchReportSchedules.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading.reportSchedules = false;
      })

      // fetchAverageTicket
      .addCase(fetchAverageTicket.pending, (state) => {
        state.loading.averageTicket = true;
        state.error = null;
      })
      .addCase(
        fetchAverageTicket.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.averageTicket = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.averageTicket = { data: payload.data };
          } else {
            // Dados paginados
            state.averageTicket = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchAverageTicket.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading.averageTicket = false;
      })

      // fetchSalesReport
      .addCase(fetchSalesReport.pending, (state) => {
        state.loading.salesReport = true;
        state.error = null;
      })
      .addCase(
        fetchSalesReport.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.salesReport = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.salesReport = { data: payload.data };
          } else {
            // Dados paginados
            state.salesReport = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchSalesReport.rejected, (state, action) => {
        state.loading.salesReport = false;
        state.error = (action.payload as { error: string }).error;
      })

      // fetchTotalSpentByClient
      .addCase(fetchTotalSpentByClient.pending, (state) => {
        state.loading.totalSpentByClient = true;
        state.error = null;
      })
      .addCase(
        fetchTotalSpentByClient.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.totalSpentByClient = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.totalSpentByClient = { data: payload.data };
          } else {
            // Dados paginados
            state.totalSpentByClient = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchTotalSpentByClient.rejected, (state, action) => {
        state.loading.totalSpentByClient = false;
        state.error = (action.payload as { error: string }).error;
      })

      // fetchClientSpendingByType
      .addCase(fetchClientSpendingByType.pending, (state) => {
        state.loading.clientSpendingByType = true;
        state.error = null;
      })
      .addCase(
        fetchClientSpendingByType.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.clientSpendingByType = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.clientSpendingByType = { data: payload.data };
          } else {
            // Dados paginados
            state.clientSpendingByType = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchClientSpendingByType.rejected, (state, action) => {
        state.loading.clientSpendingByType = false;
        state.error = (action.payload as { error: string }).error;
      })

      .addCase(fetchStatisticalReport.pending, (state) => {
        state.loading.statisticalReport = true;
        state.error = null;
      })
      .addCase(
        fetchStatisticalReport.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading.statisticalReport = false;
          const payload = action.payload;

          if (payload.data && Array.isArray(payload.data.detailed)) {
            // Aqui tem dados no formato esperado
            state.statisticalReport = {
              data: {
                totals: payload.data.totals,
                detailed: payload.data.detailed,
              },
              // Se tiver paginação, você pode adicionar
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          } else if (Array.isArray(payload.data)) {
            // Se vier só array
            state.statisticalReport = {
              data: {
                detailed: payload.data,
                totals: emptyTotals,
              },
            };
          } else {
            // fallback, caso o payload seja diferente
            state.statisticalReport = {
              data: { detailed: [], totals: emptyTotals },
            };
          }
        },
      )

      .addCase(fetchStatisticalReport.rejected, (state, action) => {
        state.loading.statisticalReport = false;
        state.error = (action.payload as { error: string }).error;
      })
      // fetchGrafics
      .addCase(fetchGrafics.pending, (state) => {
        state.loading.statisticalReport = true;
        state.error = null;
      })
      .addCase(fetchGrafics.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading.statisticalReport = false;
        const payload = action.payload;
        state.grafics = { data: payload.data };
      })
      .addCase(fetchGrafics.rejected, (state, action) => {
        state.loading.statisticalReport = false;
        state.error = (action.payload as { error: string }).error;
      });
  },
});

export default ReportsSlice.reducer;
